import {useSessionStore} from '@/stores/Session.store';

export default function goServiceFactory(services) {
  let thisService = {
    init,
    getContext,
    toContext,
    toContextWithLocation,
    toPopupContext,
    toContext_v2,
    startTaskAsync,
    getInData,
    getOutData,
    isActive,
    isCanceled,
    cancelTaskAsync,
    completeTaskAsync,
    deleteTaskAsync,
    toTarget,
    returnToSender,
    getContexts,
    getContextPath,
  };

  let sessionStore;

  return thisService;

  ////////////////////
  function init() {
    sessionStore = useSessionStore();
    let cc = services.session.getClientConfig();
    sessionStore.setGoContexts(cc.go);
  }

  function getContext(goId) {
    return sessionStore.goContexts.find(c => { return c.name === goId });
  }

  function toContext(goId, pathValues = [], params = {}, newTab = false, returnId, returnPathValues = [], returnParams = {}) {
    const goContext = getContext(goId);
    if (!goContext) {
      return Promise.reject(`Unable to find context "${goId}"`);
    }

    let returnContext = null;
    let returnPath = null;
    if (returnId) {
      returnContext = getContext(returnId);
      if (!returnContext) {
        return Promise.reject(`Unable to find return context "${returnId}"`);
      }

      services.preferences.setLocalStorageObject(returnId, {
        pathValues: returnPathValues,
        params: returnParams,
      });
      params.returnId = returnId;
    }

    let path = formatPath(goContext.path, pathValues, params);
    let href = goContext.appUrl + path;
    console.log('Go to context', href);
    if (newTab) {
      services.window.open(href, '_blank');
    }
    else {
      services.window.location.href = href;
    }
  }

  function startTaskAsync(goId, config, returnConfig) {
    if (!goId) {
      console.warn('Go Task: Missing goId');
      return false;
    }

    let task = {
      active: true,
      canceled: false,
    };

    if (config) {
      task.in = config;
    }

    // It's ok not to provide return data, goId is minimum if provided
    if (returnConfig) {
      if (!returnConfig.goId) {
        console.warn('Go Task: Missing return goId');
      }
      else {
        task.out = {
          config: returnConfig
        }
      }
    }

    return services.preferences.setOrgPreferenceAsync(goId, task, 'go').then(() => {
      toContext(goId, config?.pathValues, config?.params, !!config?.newTab);
    });
  }

  function getInData(goId) {
    let task = services.preferences.getOrgPreference(goId, 'go');
    return task?.active ? task?.in?.data : null;
  }

  function getOutData(goId) {
    let task = services.preferences.getOrgPreference(goId, 'go');
    return !task?.active ? task?.out?.data : null;
  }

  function isActive(goId) {
    let task = services.preferences.getOrgPreference(goId, 'go');
    return task?.active;
  }

  function isCanceled(goId) {
    let task = services.preferences.getOrgPreference(goId, 'go');
    return task?.canceled;
  }

  function cancelTaskAsync(goId, data = null) {
    let task = services.preferences.getOrgPreference(goId, 'go');
    task.active = false;
    task.canceled = true;
    if (data) {
      if (!task.out) {
        task.out = {};
      }
      task.out.data = data
    }
    return services.preferences.setOrgPreferenceAsync(goId, task, 'go').then(() => {
      let config = task.out.config;
      if (config) {
        toContext(config.goId, config.pathValues, config.params, !!config?.newTab);
      }
      else {
        // No receiver of data so we can clean up
        return deleteTaskAsync(goId);
      }
    });
  }

  function completeTaskAsync(goId, data = null) {
    let task = services.preferences.getOrgPreference(goId, 'go');
    if (!task) {
      return Promise.resolve();
    }

    task.active = false;
    task.canceled = false;
    if (data) {
      if (!task.out) {
        task.out = {};
      }
      task.out.data = data
    }

    return services.preferences.setOrgPreferenceAsync(goId, task, 'go').then(() => {
      let config = task.out?.config;
      if (config) {
        toContext(config.goId, config.pathValues, config.params, !!config?.newTab);
      }
      else {
        // No receiver of data so we can clean up
        return deleteTaskAsync(goId);
      }
    });
  }

  function deleteTaskAsync(goId) {
    return services.preferences.deleteOrgPreferenceAsync(goId, 'go');
  }

  function formatPath(path, pathValues, params) {
    let mySubs = [];
    let myPath = null
    if (pathValues) {
      if (typeof pathValues === 'string') {
        mySubs = [pathValues];
      } else {
        mySubs = [...pathValues];
      }
    }

    const messageFormatRegex = /{(\w+)(?:,(\w+))?}/g;
    myPath = path.replace(messageFormatRegex, (match, name, formatType, index) => {
      let subIndex = isNaN(name) ? -1 : Number(name);
      if (subIndex !== -1 && mySubs.length -1 >= subIndex) {
        return mySubs[subIndex];
      }
      else {
        return `{missing-value-${name}}`;
      }
    });

    for (const [key, value] of Object.entries(params)) {
      myPath += myPath.indexOf('?') < 0 ? `?${key}=${value}` : `&${key}=${value}`;
    }
    return myPath;
  }

  function toPopupContext(goId, target, params) {
    toContext(goId, target, params,true);
  }

  function getContextPath(goId, pathValues = [], params = []) {
    let contexts = sessionStore.goContexts;
    const goContext = contexts.find(c => {
      return c.name === goId;
    });
    if (!goContext) {
      return Promise.reject(`Unable to find goId "${goId}"`);
    }

    let path = formatPath(goContext.path, pathValues, params);
    return goContext.appUrl + path;
  }

  /**
   * Navigates to a context with the target as the current location.
   * Useful for navigating back to the current location.
   * For example, closing My Settings in home will navigate back to the current location.
   *
   * @param context to navigate to
   */
  function toContextWithLocation(goId, params = []) {
    let keys = params.map(p => {
      return p.key;
    });
    let href = services.window.location.href;

    let target;
    let myIndex = href.indexOf('?');
    if (myIndex !== -1) {
      let builder = href.substr(0, myIndex);
      let paramStr = href.substr(myIndex+1);
      let hrefParams = paramStr.split('&');
      let paramIndex = 0;
      for (let i = 0; i < hrefParams.length; i++) {
        let key = hrefParams[i].split('=')[0];
        if (key === 'returnUrl' || keys.includes(key)) {
          continue;
        }

        builder = paramIndex === 0 ? `${builder}?${hrefParams[i]}` : `${builder}&${hrefParams[i]}`;
      }

      target = builder;
    }
    else {
      target = href;
    }

    target = services.window.encodeURIComponent(target);
    let myParams = {};
    params.forEach(param => {
      myParams[param.key] = param.value;
    });
    myParams.target = target;

    return toContext(goId, [target], myParams);
  }

  function toContext_v2(toContext, toParams = {}, fromContext, fromParams = {}, newTab = false) {
    const contexts = sessionStore.goContexts;
    const to = contexts.find(c => {
      return c.name === toContext;
    });

    const from = contexts.find(c => {
      return c.name === fromContext;
    });

    if (!to) {
      console.warn(`Go: Context ${toContext} not found`, contexts);
      return;
    }

    let myToPath = to.path;
    let myToParams = Object.assign({}, toParams);
    if (myToPath.includes('{0}') && myToParams.pathParam) {
      if (myToParams.pathParam) {
        myToPath = myToPath.replace('{0}', myToParams.pathParam);
        delete myToParams.pathParam;
      }
      else {
        console.warn(`Go: Context ${toContext} requires toParams.pathParam`, to.path);
        return;
      }
    }

    if (from) {
      toTarget(to.appId, myToPath, myToParams, from.appId, from.path, fromParams);
    }
    else {
      toTarget(to.appId, myToPath, myToParams, null, null, {}, newTab);
    }
  }

  function toTarget(toAppId, toPath, toParams = {}, fromAppId, fromPath, fromParams = {}, newTab = false) {
    if (fromAppId) {
      services.preferences.setLocalStorageObject(fromAppId, {
        appId: fromAppId,
        path: fromPath,
        params: fromParams,
      });
    }

    console.log('PrintOS Header Redirect - toTarget');
    _buildAndGo(toAppId, toPath, toParams, fromAppId, newTab);
  }

  function returnToSender(returnId) {
    let fromData = services.preferences.getLocalStorageObject(returnId);
    if (fromData) {
      services.preferences.deleteLocalStorage(returnId);
      toContext(returnId, fromData.pathValues, fromData.params);
      console.log('PrintOS Header Redirect - returnToSender');
      return true;
    }

    return false;
  }

  function _buildAndGo(appId, path, params, fromId = null, newTab = false) {
    const app = services.app.getAppById(appId);
    let paramStr = '';
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        paramStr += paramStr === '' ? `?${key}=${value}` : `&${key}=${value}`;
      }
    }
    if (fromId) {
      paramStr += paramStr === '' ? `?fromId=${fromId}` : `&fromId=${fromId}`;
    }
    const toUrl = `${app.appUrl}${path}${paramStr}`;
    if (newTab) {
      window.open(toUrl, '_blank');
    }
    else {
      window.location.href = toUrl;
    }
  }

  function getContexts() {
    return sessionStore.goContexts;
  }
}
